import React, { useContext, useEffect, useMemo, useRef } from 'react'
import { Collapse } from '../../components/collapse'
import { collapsePanelContext } from '../../components/collapse-toggle-panel'
import { renderObjectToDataRows } from '../../helpers/object-to-data-rows'
import { useOpened } from '../../hooks/use-opened'
import { useExport } from '../../contexts'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { AdditionalInfo, MetaDataItem, ProcessList, formatId, getActionIconData, getSecondaryText } from './helpers'
import { camelCaseToWords, scrollTo } from 'app/modules/shared/helpers'
import { IconInteresting } from 'app/components/icon-interesting'
import { applicationConfiguration, useURLSearchParams } from 'app/modules/shared'
import { SignalVerdict, threatVerdicts } from '../../types'
import { getVerdictLabel } from '../../helpers'

interface CollapsibleEmulationItemProps {
  item: { [key: string]: any }
  idx: number
  opened?: boolean
}
export function CollapsibleEmulationItem(props: CollapsibleEmulationItemProps): JSX.Element {
  const { item, idx, opened } = props
  const collapsePanel = useContext(collapsePanelContext)
  const { isOpen, uniqueIdx } = collapsePanel.getCollapseBlockState(idx, item.interesting)
  const collapseState = collapsePanel.getCollapsesState()
  const open = useOpened(!!opened, isOpen, collapseState)
  const iconData = useMemo(() => getActionIconData(item.action), [item.action])
  const itemId = useURLSearchParams().get('item')
  const id = formatId(item.dataUUID)
  const header = <EmulationItemHeader item={item} isScrollToActivated={id === itemId} />
  const body = <EmulationItemBody item={item} />

  return (
    <Collapse
      id={id}
      header={header}
      body={body}
      key={uniqueIdx}
      className={'w-75'}
      isOpen={open || itemId === id}
      iconImageClass={iconData?.iconImageClass}
      iconClass={iconData?.iconCategoryClass}
      hasTimelineIcon
    />
  )
}

function EmulationItemHeader({
  item,
  graphPopover,
  isScrollToActivated,
}: {
  item: { [key: string]: any }
  graphPopover?: boolean
  isScrollToActivated?: boolean
}): JSX.Element {
  const { exporting } = useExport()
  const secondaryText = getSecondaryText(item, exporting)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  useEffect(() => {
    if (ref?.current && isScrollToActivated) {
      // Scroll to emulation item, in case user clicked on corresponding node in graph.
      // Timeout is needed because some time is required for another uncollapsed item to be collapsed,
      // otherwise scroll position won't be correct.
      setTimeout(() => scrollTo(ref, 'start'), 500)
    }
  }, [isScrollToActivated])

  return (
    <>
      <div className={'d-flex ' + (graphPopover ? 'mb-3' : '')} ref={ref} style={{ scrollMarginTop: '100px' }}>
        {camelCaseToWords(item.action)}
        {item.interesting ? <IconInteresting className="ms-2" /> : null}
      </div>
      {secondaryText && !graphPopover ? (
        <div style={{ width: '30%' }}>
          <div className="ellipsis">{secondaryText}</div>
        </div>
      ) : null}
    </>
  )
}

function EmulationItemBody({ item }: { item: { [key: string]: any } }): JSX.Element {
  const { _t } = useTypedTranslation()
  const { useHumanFriendlyVerdicts } = useContext(applicationConfiguration)

  const data: { [key: string]: any } = {}
  let additionalInfo: null | { [key: string]: any } = null
  let metaData = null
  let processList = null

  Object.entries(item).forEach(([key, value]) => {
    if (['dataUUID', 'commandLowerForFilter', 'category', 'interesting', 'action'].includes(key)) {
      return
    }
    if (key === 'additionalInformation') {
      additionalInfo = { ...value }
    } else if (key === 'metaData') {
      metaData = value
    } else if (key === 'verdictInfo' && threatVerdicts.includes(value.verdict as SignalVerdict)) {
      data['verdict'] = getVerdictLabel(value.verdict as SignalVerdict, useHumanFriendlyVerdicts)
      data['threatIndicator'] = `${value.signalId} - ${value.signalDescription}`
    } else {
      data[key] = value
    }
  })

  if (additionalInfo && typeof additionalInfo['ProcessList'] !== 'undefined') {
    processList = additionalInfo['ProcessList']
    delete additionalInfo['ProcessList']
  }

  const dataComponents = renderObjectToDataRows(data)

  return (
    <>
      <div className="row-as-column compact data-blocks-no-gap">
        {Object.keys(data).length ? dataComponents : null}
        {additionalInfo ? <AdditionalInfo additionalInfo={additionalInfo} /> : null}
        {metaData ? <MetaDataItem meta={metaData} /> : null}
      </div>
      {processList ? (
        <div>
          <div className="h4">{_t('process-list')}</div>
          <ProcessList data={processList} />
        </div>
      ) : null}
    </>
  )
}

export function EmulationItem({ item }: { item: { [key: string]: any } }): JSX.Element {
  return (
    <>
      <EmulationItemHeader item={item} graphPopover={true} />
      <EmulationItemBody item={item} />
    </>
  )
}
